import { API_ENDPOINTS } from 'services/api';
import httpClient from 'services/httpClient';

export interface CoreCompetencyPayload {
  businessUnitId: string;
  businessUnitName: string;
  jobFamilyId: string;
  jobFamilyName: string;
  managementLevel: string;
  competencyType: string;
  schoolName: string;
  jobPosition?: string;
  jobPositions?: Array<{ id: string; name: string }>;
  competencyName: string;
  category: string;
  level: number;
  categoryDescription?: string;
  levelDescription?: string;
  description?: string;
  keyAction?: string;
  note?: string;
  courses: Array<{
    courseId: string;
    courseName: string;
    positions: string[];
  }>;
}

export interface CoreCompetencyUpdatePayload {
  msCompetencyManagementID: string;
  businessUnitId: string;
  businessUnitName: string;
  jobFamilyId: string;
  jobFamilyName: string;
  managementLevel: string;
  competencyType: string;
  schoolName: string;
  jobPosition?: string;
  jobPositions?: Array<{ id: string; name: string }>;
  competencyName: string;
  category: string;
  level: number;
  categoryDescription?: string;
  levelDescription?: string;
  description?: string;
  keyAction?: string;
  note?: string;
  courses: Array<{
    courseId: string;
    courseName: string;
    positions: string[];
  }>;
}

export interface CoreCompetencyResponse {
  competencyDescription: any;
  id: string;
  businessUnitId: string;
  businessUnitName: string;
  jobFamilyId: string;
  jobFamilyName: string;
  managementLevel: string;
  competencyType: string;
  schoolName: string;
  jobPosition: string;
  jobPositions?: Array<{ id: string; name: string }>;
  competencyName: string;
  category: string;
  level: number;
  categoryDescription?: string;
  levelDescription?: string;
  description?: string;
  keyAction?: string;
  note?: string;
  year?: number;
  courses: Array<{
    id: string;
    name: string;
    positions: string[];
    learningObjectives?: string | null;
    learningHours?: number | null;
    modeOfDelivery?: string | null;
    flaggingLIA?: boolean | null;
    flaggingLIM?: boolean | null;
  }>;
  createdAt: string;
  dateIn?: string;
  userIn?: string;
}

export interface CompetencyDetailApiResponse {
  message: string;
  data: {
    msCompetencyManagementID: string;
    businessUnitId: string;
    businessUnitName: string;
    jobFamilyId: string;
    jobFamilyName: string;
    managementLevel: string;
    competencyType: string;
    schoolName: string;
    jobPosition: string;
    jobPositions?: Array<{ id: string; name: string }>;
    competencyName: string;
    competencyDescription: any;
    category: string;
    level: number;
    categoryDescription?: string;
    levelDescription?: string;
    description?: string;
    keyAction?: string;
    note?: string;
    year?: number;
    dateIn?: string;
    userIn?: string;
    courses: Array<{
      courseId: string;
      courseName: string;
      positions: string[];
      learningObjectives?: string | null;
      learningHours?: number | null;
      modeOfDelivery?: string | null;
      flaggingLIA?: boolean | null;
      flaggingLIM?: boolean | null;
      flaggingC?: boolean | null;
    }>;
  };
}

export interface CompetencyListParams {
  pageNumber?: number;
  pageSize?: number;
  competencyType?: string;
  jobFamilyId?: string;
  managementLevel?: string;
  businessUnitId?: string;
  search?: string;
  competencyId?: string;
  onlyUnassigned?: number;
}

export interface CompetencyListResponse {
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
  message: string;
  data: Array<{
    year?: number;
    msCompetencyManagementID: string;
    businessUnitId: string;
    businessUnitName: string;
    jobFamilyId: string;
    jobFamilyName: string;
    managementLevel: string;
    competencyType: string;
    schoolName: string;
    jobPosition?: string;
    jobPositions?: Array<{ id: string; name: string }>;
    competencyName: string;
    category: string;
    level: number;
    categoryDescription?: string;
    levelDescription?: string;
    dateIn?: string;
    dateUp?: string;
    userIn?: string;
    userUp?: string;
    courses: Array<{
      courseId: string;
      courseName: string;
      positions: string[];
    }>;
  }>;
}

export interface CompetencyNameListParams {
  category: string;
  jobband: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface CompetencyNameItem {
  competencyID: string;
  competencyName: string;
  competencyDescription: string;
}

export interface CompetencyNameListResponse {
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalRecords: number;
  message: string;
  data: CompetencyNameItem[];
}

export const coreCompetencyService = {
  list: async (params: CompetencyListParams): Promise<CompetencyListResponse> => {
    const response = await httpClient.get<CompetencyListResponse>(
      API_ENDPOINTS.DACUM.COMPETENCY_MANAGEMENT.PAGINATED_LIST,
      { params }
    );
    return response.data;
  },

  create: async (data: CoreCompetencyPayload) => {
    const response = await httpClient.post(API_ENDPOINTS.DACUM.COMPETENCY_MANAGEMENT.ADD, data);
    return response.data;
  },

  update: async (id: string, data: CoreCompetencyPayload) => {
    const updatePayload: CoreCompetencyUpdatePayload = {
      msCompetencyManagementID: id,
      ...data
    };

    const response = await httpClient.put(
      API_ENDPOINTS.DACUM.COMPETENCY_MANAGEMENT.UPDATE,
      updatePayload
    );
    return response.data;
  },

  getById: async (id: string): Promise<CoreCompetencyResponse> => {
    const response = await httpClient.get<CompetencyDetailApiResponse>(
      API_ENDPOINTS.DACUM.COMPETENCY_MANAGEMENT.DETAIL.replace('{id}', id)
    );

    const apiData = response.data.data;
    return {
      id: apiData.msCompetencyManagementID,
      businessUnitId: apiData.businessUnitId,
      businessUnitName: apiData.businessUnitName,
      jobFamilyId: apiData.jobFamilyId,
      jobFamilyName: apiData.jobFamilyName,
      managementLevel: apiData.managementLevel,
      competencyType: apiData.competencyType,
      schoolName: apiData.schoolName,
      jobPosition: apiData.jobPosition,
      jobPositions: apiData.jobPositions,
      competencyName: apiData.competencyName,
      competencyDescription: apiData.competencyDescription,
      category: apiData.category,
      level: apiData.level,
      categoryDescription: apiData.categoryDescription,
      levelDescription: apiData.levelDescription,
      description: apiData.description,
      keyAction: apiData.keyAction,
      note: apiData.note,
      year: apiData.year,
      courses: apiData.courses.map((course) => ({
        id: course.courseId,
        name: course.courseName,
        positions: course.positions || [],
        learningObjectives: course.learningObjectives,
        learningHours: course.learningHours,
        modeOfDelivery: course.modeOfDelivery,
        flaggingLIA: course.flaggingLIA,
        flaggingLIM: course.flaggingLIM
      })),
      createdAt: apiData.dateIn || '',
      dateIn: apiData.dateIn,
      userIn: apiData.userIn
    };
  },

  delete: async (id: string) => {
    const response = await httpClient.delete(API_ENDPOINTS.DACUM.COMPETENCY_MANAGEMENT.DELETE, {
      data: {
        msCompetencyManagementID: id
      }
    });
    return response.data;
  },

  listCompetencyNames: async (
    params: CompetencyNameListParams
  ): Promise<CompetencyNameListResponse> => {
    const response = await httpClient.get<CompetencyNameListResponse>(
      API_ENDPOINTS.DACUM.COMPETENCY_MANAGEMENT.LIST_COMPETENCY_NAME,
      { params }
    );
    return response.data;
  }
};
